import React, {
  useState, useEffect, useMemo, useLayoutEffect, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classes from './physicians.module.scss';
import Button from '../../_common/Form/Buttons/Button';
import InputField from '../../_common/Form/TextFields/InputField';
import Select from '../../_common/Form/Selects/Select/Select';
import { specOptions } from '../../../utils/data/data';
import { useQuery } from '../../../utils/hooks/useQuery';
import { changeMenuPath } from '../../../store/actions/menu';
import utils from '../../../utils/utils';
import { getPhysiciansRequest } from '../../../store/actions/physicians';
import PhysiciansTable from './PhysiciansTable/PhysiciansTable';
import Loader from '../../_common/Loader/Loader';
import MergeModalContent from './MergeModalContent/MergeModalContent';
import Modal from '../../_common/Modal/Modal';
import { getHealthRequest } from '../../../store/actions/healths';
import AccountHelpers from '../../../utils/accountHelpers';
import { getSelectRepListRequest } from '../../../store/actions/users';
import accountHelpers from '../../../utils/accountHelpers';

const headerPhysician = [
  {
    name: 'Name',
    path: 'name',
  },
  {
    name: 'Group',
    path: 'group',
  },
  {
    name: 'Rep',
    path: 'rep',
  },
  {
    name: 'Specialization',
    path: 'specialization',
  },
  {
    name: 'Phone',
    path: 'phone',
  },
  {
    name: 'Address',
    path: 'address',
  },
];

const headerHealth = [
  {
    name: 'Name',
    path: 'name',
  },
  {
    name: 'Hub Id',
    path: 'hubId',
  },
  {
    name: 'Rep',
    path: 'rep',
  },
  {
    name: 'Phone',
    path: 'phone',
  },
  {
    name: 'Address',
    path: 'address',
  },
];

const limitRep = 20;

const Physicians = ({ health }) => {
  const isAdmin = AccountHelpers.isInRole('Admin');

  const hasUpdatePermission = !health && accountHelpers.isInOneOfRoles(['Admin', 'Pharmacy', 'Rep', 'Health']);
  const hasHealthUpdatePermission = health && AccountHelpers.getUser().role !== 'Rep';

  const { pathname } = useLocation();
  const { query, setQuery, queryToStringify } = useQuery();

  const firstLoad = useRef(true);

  const dispatch = useDispatch();
  const { physiciansList, physiciansTotalPage } = useSelector((store) => store.physicians);
  const { healthList, healthTotalPage } = useSelector((store) => store.healths);
  const { selectRepList, pageCount, currentPage } = useSelector((store) => store.users);

  const initialData = (setWithQuery) => {
    let data = {
      userId: AccountHelpers.getUser().id,
      page: 1,
      limit: query.limit || 15,
      name: '',
      address: '',
      phone: '',
    };

    if (health) {
      data.hubId = '';
    } else {
      data.searchGroup = '';
      data.searchSpec = '';
    }

    if (setWithQuery) {
      data = _.forIn(data, (value, key) => {
        if (key === 'limit') {
          data[key] = query[key] || 15;
        } else if (key === 'page') {
          data[key] = query[key] || 1;
        } else {
          data[key] = query[key] || '';
        }
      });
    }

    return data;
  };

  const [requestData, setRequestData] = useState(initialData(true));

  const {
    page, limit, searchGroup, address, phone, searchSpec, name, hubId,
  } = requestData;

  const [loading, setLoading] = useState(true);
  const [isMergeModalOpen, mergeModalToggle] = useState(false);

  const header = useMemo(() => {
    const physicianHeader = AccountHelpers.getUser().role === 'Health'
      ? [
        {
          name: 'Name',
          path: 'name',
        },
        {
          name: 'Phone',
          path: 'phone',
        },
        {
          name: 'Address',
          path: 'address',
        },
      ]
      : headerPhysician;

    return health ? headerHealth : physicianHeader;
  }, [AccountHelpers.getUser().role]);

  useLayoutEffect(() => {
    firstLoad.current = true;
  }, [pathname]);

  useLayoutEffect(() => {
    setLoading(true);

    if (firstLoad.current) {
      dispatch(getSelectRepListRequest({ limit: limitRep, role: 'Rep' }));
    }

    const sendingData = utils.deleteEmptyKeys({ ...requestData });

    const timeout = setTimeout(async () => {
      if (health) {
        await dispatch(getHealthRequest(sendingData));
      } else {
        await dispatch(getPhysiciansRequest(sendingData));
      }

      delete sendingData.userId;
      setQuery(sendingData);
      setLoading(false);

      firstLoad.current = false;
    }, 500);

    return () => clearTimeout(timeout);
  }, [pathname, requestData]);

  useEffect(() => {
    dispatch(changeMenuPath(health ? 'Health' : 'Physicians', `${pathname}${queryToStringify(query)}`));
  }, [query]);

  const changeRequestData = async (path, value) => {
    setRequestData((prev) => {
      const newData = { ...prev, [path]: value };

      if (path !== 'page') {
        newData.page = 1;
      }

      return newData;
    });
  };

  const clearButtonClick = async () => {
    setRequestData(initialData());
  };

  const nextOptionListRequest = async (repPage) => {
    await dispatch(getSelectRepListRequest({ page: repPage, limit: limitRep, role: 'Rep' }));
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <h2>{health ? 'Select an Health Plan' : 'Select a Physician'}</h2>

        <div className={`${health ? classes.health_header_button : classes.header_button}`}>
          { hasUpdatePermission && (
            <Button
              className={classes.merge_btn}
              title="Merge physician"
              type="button"
              onClick={() => mergeModalToggle(true)}
            />
          )}

          {(hasUpdatePermission || hasHealthUpdatePermission) && (
            <Button
              className={classes.add_btn}
              title={health ? 'Add a new Health Plan' : 'Add a new Physician'}
              type="button"
              href="add"
              reverseColor
              addButton
            />
          )}
        </div>
      </div>

      <div className={classes.filters}>
        <form
          className={classes.fields_wrapper}
          onSubmit={() => {
          }}
        >
          <div className={classes.fields_group}>
            <div className={classes.field_wrapper}>
              <InputField
                label="Name"
                value={name}
                onChange={(value) => changeRequestData('name', value)}
              />
            </div>

            <div className={classes.field_wrapper}>
              <InputField
                label={health ? 'Hub Id' : 'Group'}
                value={health ? hubId : searchGroup}
                onChange={(value) => changeRequestData(health ? 'hubId' : 'searchGroup', value)}
              />
            </div>

          </div>

          <div className={classes.fields_group}>
            <div className={classes.field_wrapper}>
              <InputField
                label="Address"
                value={address}
                onChange={(value) => changeRequestData('address', value)}
              />
            </div>

            <div className={health ? classes.field_wrapper_health : classes.field_wrapper}>
              <InputField
                label="Phone"
                value={phone}
                onChange={(value) => changeRequestData('phone', value)}
              />
            </div>

            {!health && isAdmin && (
              <div className={classes.field_wrapper}>
                <Select
                  label="All reps"
                  nextOptionListRequest={nextOptionListRequest}
                  options={selectRepList}
                  totalPage={pageCount}
                  currentPage={currentPage}
                  limit={limitRep}
                  currentOption={requestData.rep}
                  onChange={(option) => changeRequestData('rep', option)}
                  optionPathname="name"
                />
              </div>
            )}

            {!health && (
              <div className={classes.field_wrapper}>
                <Select
                  label="Specialization"
                  options={specOptions}
                  currentOption={searchSpec}
                  onChange={(option) => changeRequestData('searchSpec', option)}
                />
              </div>
            )}

            <div className={classes.btns_wrapper}>

              <Button
                title="Clear"
                type="button"
                clearButton
                onClick={clearButtonClick}
              />
            </div>
          </div>

        </form>

        {/* <div className={classes.assign_btn_wrapper}> */}
        {/*  <Button */}
        {/*    title="Assign to Me" */}
        {/*    className={classes.assign_btn} */}
        {/*    type="button" */}
        {/*    reverseColor */}
        {/*  /> */}
        {/* </div> */}
      </div>
      {firstLoad.current
        ? <Loader className={classes.loader} />

        : (
          <PhysiciansTable
            data={health ? healthList : physiciansList}
            totalPage={health ? +healthTotalPage : +physiciansTotalPage}
            page={+page - 1}
            onChange={changeRequestData}
            limit={+limit}
            loading={loading}
            header={header}
            health={health}
          />
        )}

      <Modal
        open={isMergeModalOpen}
        onClose={() => mergeModalToggle(false)}
        className={classes.merge_modal}
      >
        <MergeModalContent
          onCancel={() => mergeModalToggle(false)}
        />
      </Modal>
    </div>
  );
};

Physicians.propTypes = {
  health: PropTypes.bool,
};

Physicians.defaultProps = {
  health: false,
};

export default Physicians;
